import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDispatchApp, useSelectorApp } from "../lib/redux";
import {
  startGetAdvertisersPaginated,
  startGetAdvertiserFullData,
  getAdvertisers,
  startGetAgenciesNames,
  startPersistAdvertiserHealthScore,
} from "../actions/owner";
import {
  CLIENT_TEXT_SEARCH_FILTER_TYPE,
  isClientTextSearch,
} from "../utils/clientTextSearch";
import { calculateHealthyScore } from "../pages/AdminDashboard/utils/healthScoreCalculations";
import {
  getAgencyHealthFromAdvertisers,
  mapAgencyGroupsWithSortedAdvertisers,
} from "../pages/AdminDashboard/utils/agencyHealthSummary";
import { getEffectiveKpiSummary } from "../pages/AdminDashboard/utils/kpiSummaryMerge";
import { hasHiddenDefaultClientsListPlanPrice } from "../pages/AdminDashboard/utils/clientsListVisibility";
import { resolveAgencyImageProvider } from "../pages/AdminDashboard/imageProviders";

import { LaunchError } from "../utils/errors";

/**
 * =============================================================================
 * QUEUE SYSTEM DOCUMENTATION
 * =============================================================================
 *
 * This hook implements a queue-based system for fetching advertiser data
 * to optimize API quota usage. Instead of fetching all data for all advertisers
 * at once, it processes them sequentially (or with configurable concurrency).
 *
 * FLOW:
 * ─────
 * 1. Initial Load:
 *    - startGetAdvertisersPaginated fetches basic advertiser data (name, plan, etc.)
 *    - Each new advertiser is added to the queue with isLoading: true
 *
 * 2. Queue Processing:
 *    - pumpQueue takes N items (based on QUEUE_CONCURRENCY)
 *    - For each item, startGetAdvertiserFullData fetches complete data:
 *      • Login metrics (logins last 30 days, days since last login)
 *      • Active campaigns count
 *      • Optimizations count
 *      • Performance metrics (spend, CPA, ROAS)
 *      • NPS rating
 *      • Active users (cached by agencyID to avoid duplicate queries)
 *
 * 3. Completion:
 *    - When processing completes, isLoading is set to false
 *    - The advertiser data is consolidated and UI updates
 *    - Next item in queue starts processing automatically
 *
 * STATES:
 * ───────
 * - queueRef: Advertiser IDs waiting to be processed (FIFO)
 * - inFlightIdsRef: Advertiser IDs currently being processed
 * - advertiserFullData: Consolidated data for each advertiser
 *
 * CACHING:
 * ────────
 * - agencyCacheRef + agencyCache: Unified cache by agencyID
 *   { users, login metrics, activeUsers, name, isAppSumo, discoverySource, usersLoaded, profileLoaded }
 * - agenciesExpandedRef: Pagination dedup — agencies already expanded in paginated fetch
 * - processedAdvertisersRef: Tracks which advertisers have been queued
 *   to prevent duplicate processing on re-renders
 *
 * CONFIGURATION:
 * ──────────────
 * - QUEUE_CONCURRENCY: Number of advertisers to process simultaneously
 *   Default: 2 (increase for faster loading, decrease to reduce API load)
 *
 * =============================================================================
 */

// Configurable concurrency for queue processing
const QUEUE_CONCURRENCY = 4;
const MIN_SEARCH_LENGTH = 1;

const isValidAgencyIDForGrouping = (agencyID) =>
  agencyID && agencyID !== "unknown" && String(agencyID).trim() !== "";

const matchesClientsListStatusFilter = ({ advertiser, filterStatus }) => {
  if (filterStatus === true) {
    return (
      advertiser?.isPlanActive === true || advertiser?.isPendingPayment === true
    );
  }
  return advertiser?.isPlanActive === filterStatus;
};
const INITIAL_PAGE_SIZE = 100;
const LOAD_MORE_PAGE_SIZE = 100;
const getFilterType = (filter) => {
  if (filter === null || filter === undefined) {
    return null;
  }

  if (typeof filter === "boolean") {
    return "isPlanActive";
  }

  if (typeof filter === "string" && filter.length >= MIN_SEARCH_LENGTH) {
    // Free-text client search: business name + email indexes share this filterType.
    return CLIENT_TEXT_SEARCH_FILTER_TYPE;
  }

  // Future: Add more filter type mappings here
  // if (typeof filter === 'string' && filter.startsWith('plan:')) {
  //   return "subscriptionPlan";
  // }

  return null;
};

/**
 * Custom hook to manage clients list with pagination and queue-based data fetching
 *
 * @returns {Object} Hook return values:
 * - advertisers: Consolidated advertiser data with isLoading state
 * - displayedClients: Array format for table rendering [{ id, advertiser }]
 * - isLoading: Initial page load state
 * - isLoadingMore: Infinite scroll loading state
 * - hasMoreItems: Whether more items can be loaded
 * - loadMoreClients: Function to trigger infinite scroll
 * - totalClients: Count of loaded clients
 */
const useClientsList = () => {
  const dispatch = useDispatchApp();
  const { advertisers: reduxAdvertisers } = useSelectorApp(
    (state) => state.owner
  );
  const reduxAdvertisersRef = useRef(reduxAdvertisers);
  reduxAdvertisersRef.current = reduxAdvertisers;

  // Queue system (internal — refs only; no React state needed for the worker queue)
  const queueRef = useRef([]);
  const queueIdsRef = useRef(new Set());
  const inFlightCountRef = useRef(0);
  const inFlightIdsRef = useRef(new Set());
  const loadedAdvertiserIdsRef = useRef(new Set());
  const pumpQueueRef = useRef(() => {});
  // Unified agency cache (users + RTDB profile). Ref for sync/async writes; state for UI updates.
  const agencyCacheRef = useRef({});
  // Pagination dedup — not agency payload (tracks expanded agencies per fetch run)
  const agenciesExpandedRef = useRef({});
  const processedAdvertisersRef = useRef({});
  const currencyRatesCacheRef = useRef({});

  const [advertiserFullData, setAdvertiserFullData] = useState({});
  const [agencyCache, setAgencyCache] = useState({});

  const getAgencyEntry = useCallback(
    (agencyID) => {
      if (!agencyID) return null;
      return agencyCache[agencyID] ?? agencyCacheRef.current[agencyID] ?? null;
    },
    [agencyCache]
  );

  const getAgencyProfile = useCallback(
    (agencyID) => {
      const entry = getAgencyEntry(agencyID);
      if (!entry?.profileLoaded) return null;
      return {
        name: entry.name,
        isAppSumo: entry.isAppSumo ?? false,
        discoverySource: entry.discoverySource ?? null,
        imageProvider: resolveAgencyImageProvider(entry.imageProvider),
      };
    },
    [getAgencyEntry]
  );

  const mergeAgencyCache = useCallback(
    (agencyID, patch, { syncState = false } = {}) => {
      if (!agencyID) return;
      agencyCacheRef.current[agencyID] = {
        ...agencyCacheRef.current[agencyID],
        ...patch,
      };
      if (syncState) {
        setAgencyCache((prev) => ({
          ...prev,
          [agencyID]: agencyCacheRef.current[agencyID],
        }));
      }
    },
    []
  );

  // Pagination state
  const [lastKey, setLastKey] = useState(null);
  const [emailIndexLastKey, setEmailIndexLastKey] = useState(null);
  const [loadedLastAdvertiser, setLoadedLastAdvertiser] = useState(false);
  const [loadedLastEmailIndex, setLoadedLastEmailIndex] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Filter state
  const [filterStatus, setFilterStatus] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const currentFilterSignatureRef = useRef(null);
  const [loadedFilterSignature, setLoadedFilterSignature] = useState(null);
  const fetchGenerationRef = useRef(0);
  const didMountInitialFetchRef = useRef(false);

  const getEffectiveFilter = useCallback(
    ({
      searchTermValue = searchTerm,
      filterStatusValue = filterStatus,
    } = {}) => {
      const hasActiveSearch =
        searchTermValue && searchTermValue.length >= MIN_SEARCH_LENGTH;
      const hasActiveFilter = filterStatusValue !== null;
      const filterValue = hasActiveSearch
        ? searchTermValue.toLowerCase().trim()
        : hasActiveFilter
          ? filterStatusValue
          : null;
      const filterType = getFilterType(filterValue);
      const signature = `${filterType ?? "none"}::${String(filterValue)}`;

      return { filterValue, filterType, signature };
    },
    [searchTerm, filterStatus]
  );

  const processAdvertiserRef = useRef(() => {});

  const pumpQueue = useCallback(() => {
    const toStart = [];
    const remaining = [];

    for (const id of queueRef.current) {
      if (
        toStart.length < QUEUE_CONCURRENCY - inFlightCountRef.current &&
        !inFlightIdsRef.current.has(id)
      ) {
        toStart.push(id);
      } else {
        remaining.push(id);
      }
    }

    if (toStart.length === 0) {
      return;
    }

    queueRef.current = remaining;

    for (const id of toStart) {
      inFlightCountRef.current += 1;
      inFlightIdsRef.current.add(id);
      queueIdsRef.current.delete(id);
      processAdvertiserRef.current(id);
    }
  }, []);

  pumpQueueRef.current = pumpQueue;

  // Process a single advertiser from the queue
  const processAdvertiser = useCallback(
    async (advertiserID) => {
      try {
        const advertiser = reduxAdvertisers[advertiserID];
        if (!advertiser) {
          // Redux not ready yet — re-queue for when paginated data lands
          if (!queueRef.current.includes(advertiserID)) {
            queueIdsRef.current.add(advertiserID);
            queueRef.current = [...queueRef.current, advertiserID];
          }
          return;
        }
        const result = await dispatch(
          startGetAdvertiserFullData(
            advertiserID,
            advertiser.agencyID,
            agencyCacheRef.current
          )
        );
        if (result?.ok) {
          const { data } = result;

          if (data.agencyID && data.agencyData) {
            mergeAgencyCache(data.agencyID, {
              ...data.agencyData,
              usersLoaded: true,
            });
          }

          // Calculate health score after getting all data
          let healthScore = null;
          try {
            const healthResult = await calculateHealthyScore(
              {
                totalLoginsLast30Days:
                  data.loginMetrics?.totalLoginsLast30Days || 0,
                lastLogin: data.loginMetrics?.daysSinceLastLogin ?? null,
                activeUsers: data.activeUsers || 0,
                metrics: data.metrics,
                activeCampaigns: data.campaigns?.activeCampaigns || 0,
                mrr: advertiser.subscriptionPlanInfo?.price || 0,
                invoiceStatus:
                  advertiser.isPlanActive === true
                    ? "paid"
                    : advertiser.isPlanActive === false
                      ? "overdue"
                      : "pending",
              },
              dispatch,
              currencyRatesCacheRef.current
            );
            healthScore = healthResult.score;

            // console.info("[HealthScore]", {
            //   advertiserID,
            //   businessName: advertiser.businessName ?? null,
            //   pillars: healthResult.pillars,
            //   total: healthResult.score,
            // });
            dispatch(
              startPersistAdvertiserHealthScore({
                advertiserID,
                healthScore,
                isPlanActive: advertiser.isPlanActive,
              })
            );
          } catch (error) {
            dispatch(
              LaunchError(error, false, [], {
                functionName: "useClientsList",
                capture: "exception",
              })
            );
            console.error(
              `Error calculating health score for ${advertiserID}:`,
              error
            );
          }

          // Update advertiser full data (mark as loaded)
          setAdvertiserFullData((prev) => ({
            ...prev,
            [advertiserID]: {
              isLoading: false,
              loginMetrics: data.loginMetrics,
              campaigns: data.campaigns,
              hasRedCampaignAlerts:
                data.campaigns?.hasRedCampaignAlerts || false,
              redCampaignAlerts: data.campaigns?.redCampaignAlerts || [],
              kpiSummary: data.campaigns?.kpiSummary || null,
              optimizations: data.optimizations,
              metrics: data.metrics,
              npsRating: data.npsRating,
              activeUsers: data.activeUsers,
              healthScore,
              aiCreativesLast30Days: data.aiCreativesLast30Days, // Nuevo campo
            },
          }));
          loadedAdvertiserIdsRef.current.add(advertiserID);
        } else {
          // Mark as loaded even on error
          setAdvertiserFullData((prev) => ({
            ...prev,
            [advertiserID]: {
              ...prev[advertiserID],
              isLoading: false,
            },
          }));
        }
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "useClientsList",
            capture: "exception",
          })
        );
        console.error(`Error processing advertiser ${advertiserID}:`, error);
        // Mark as loaded on error
        setAdvertiserFullData((prev) => ({
          ...prev,
          [advertiserID]: {
            ...prev[advertiserID],
            isLoading: false,
          },
        }));
      } finally {
        inFlightCountRef.current = Math.max(0, inFlightCountRef.current - 1);
        inFlightIdsRef.current.delete(advertiserID);
        // Defer so a "redux not ready" re-queue cannot synchronously recurse pumpQueue.
        queueMicrotask(() => pumpQueueRef.current());
      }
    },
    [dispatch, reduxAdvertisers, mergeAgencyCache]
  );

  processAdvertiserRef.current = processAdvertiser;

  const fetchInitialData = useCallback(
    async (filterValue = null, generation = null) => {
      setIsLoading(true);
      setLastKey(null);
      setEmailIndexLastKey(null);
      setLoadedLastAdvertiser(false);
      setLoadedLastEmailIndex(false);
      // Reset agency expansion cache for a clean pagination run (filters/search can change the list)
      agenciesExpandedRef.current = {};

      const filterType = getFilterType(filterValue);
      const startedSignature = `${filterType ?? "none"}::${String(filterValue)}`;
      currentFilterSignatureRef.current = startedSignature;

      const runPaginatedFetch = ({
        lastKeyArg = null,
        emailIndexLastKeyArg = null,
        restAdvertisersArg = {},
        pageSize = INITIAL_PAGE_SIZE,
        filterValueArg = filterValue,
        filterTypeArg = filterType,
      } = {}) =>
        dispatch(
          startGetAdvertisersPaginated(
            lastKeyArg,
            (newLastKey) => setLastKey(newLastKey),
            () => setLoadedLastAdvertiser(true),
            pageSize,
            restAdvertisersArg,
            filterValueArg,
            filterTypeArg,
            agenciesExpandedRef.current,
            emailIndexLastKeyArg,
            (newEmailIndexLastKey) =>
              setEmailIndexLastKey(newEmailIndexLastKey),
            () => setLoadedLastEmailIndex(true)
          )
        );

      try {
        await runPaginatedFetch();
        const isStale =
          generation != null && generation !== fetchGenerationRef.current;
        if (isStale && currentFilterSignatureRef.current !== startedSignature) {
          const {
            filterValue: currentFilterValue,
            filterType: currentFilterType,
          } = getEffectiveFilter();
          if (
            currentFilterSignatureRef.current ===
            `${currentFilterType ?? "none"}::${String(currentFilterValue)}`
          ) {
            await runPaginatedFetch({
              filterValueArg: currentFilterValue,
              filterTypeArg: currentFilterType,
            });
            setLoadedFilterSignature(currentFilterSignatureRef.current);
          }
          return;
        }
        setLoadedFilterSignature(startedSignature);
      } catch (err) {
        dispatch(
          LaunchError(err, false, [], {
            functionName: "runPaginatedFetch",
            capture: "exception",
          })
        );
        console.error("Error in fetchInitialData:", err);
      } finally {
        if (generation == null || generation === fetchGenerationRef.current) {
          setIsLoading(false);
        }
      }
    },
    [dispatch, getEffectiveFilter]
  );

  // Handle filter changes - CRITICAL: Clean refs BEFORE Redux to avoid processing stale data
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Determine which filter to use (cross-clearing is done in handlers)
    const { filterValue } = getEffectiveFilter();

    // 1. CLEAR FIRST the references to avoid processing stale data
    processedAdvertisersRef.current = {};
    queueRef.current = [];
    queueIdsRef.current = new Set();
    inFlightCountRef.current = 0;
    inFlightIdsRef.current = new Set();
    loadedAdvertiserIdsRef.current = new Set();
    agencyCacheRef.current = {};
    agenciesExpandedRef.current = {};
    currencyRatesCacheRef.current = {};

    setAdvertiserFullData({});
    setAgencyCache({});
    setLastKey(null);
    setEmailIndexLastKey(null);
    setLoadedLastAdvertiser(false);
    setLoadedLastEmailIndex(false);
    setLoadedFilterSignature(null);

    // 3. Clear Redux (this will trigger the useEffect at line 201, but processedAdvertisersRef is already clean)
    dispatch(getAdvertisers({}));

    // 4. Load new data with the filter
    setIsLoading(true);
    const generation = ++fetchGenerationRef.current;
    fetchInitialData(filterValue, generation);
  }, [
    filterStatus,
    searchTerm,
    dispatch,
    fetchInitialData,
    getEffectiveFilter,
  ]);

  // Add new advertisers to queue when they arrive.
  // Wait until the current filter's page fetch has committed (loadedFilterSignature)
  // so remounting the dashboard does not immediately process stale Redux advertisers
  // left over from a previous visit (e.g. Dashboard → Forms → Dashboard).
  useEffect(() => {
    const { signature } = getEffectiveFilter();
    if (loadedFilterSignature == null || signature !== loadedFilterSignature) {
      return;
    }

    const advertiserIDs = Object.keys(reduxAdvertisers || {});

    const newAdvertiserIDs = advertiserIDs.filter((id) => {
      if (processedAdvertisersRef.current[id]) return false;
      if (loadedAdvertiserIdsRef.current.has(id)) return false;
      if (queueIdsRef.current.has(id)) return false;
      if (inFlightIdsRef.current.has(id)) return false;
      return true;
    });

    if (newAdvertiserIDs.length > 0) {
      // Mark as processed (queued)
      newAdvertiserIDs.forEach((id) => {
        processedAdvertisersRef.current[id] = true;
        queueIdsRef.current.add(id);
      });

      // Initialize full data with isLoading: true
      setAdvertiserFullData((prev) => {
        const newData = { ...prev };
        newAdvertiserIDs.forEach((id) => {
          if (prev[id]?.isLoading === false) {
            return;
          }
          newData[id] = {
            isLoading: true,
            loginMetrics: null,
            campaigns: null,
            hasRedCampaignAlerts: false,
            redCampaignAlerts: [],
            kpiSummary: null,
            optimizations: null,
            metrics: null,
            npsRating: null,
            activeUsers: null,
            healthScore: null,
            aiCreativesLast30Days: null, // Nuevo campo inicializado
          };
        });
        return newData;
      });

      // Add to queue (FIFO order)
      queueRef.current = [...queueRef.current, ...newAdvertiserIDs];
      pumpQueueRef.current();
    }
  }, [reduxAdvertisers, loadedFilterSignature, getEffectiveFilter]);

  const loadMoreClients = useCallback(async () => {
    const { filterValue, filterType, signature } = getEffectiveFilter();
    const isTextSearch = isClientTextSearch(filterType);
    const paginationExhausted = isTextSearch
      ? loadedLastAdvertiser && loadedLastEmailIndex
      : loadedLastAdvertiser;

    if (paginationExhausted || isLoadingMore) return;

    setIsLoadingMore(true);

    // Prevent mixing cursors between different query modes.
    if (
      currentFilterSignatureRef.current &&
      currentFilterSignatureRef.current !== signature
    ) {
      setIsLoadingMore(false);
      return;
    }

    try {
      await dispatch(
        startGetAdvertisersPaginated(
          lastKey,
          (newLastKey) => setLastKey(newLastKey),
          () => setLoadedLastAdvertiser(true),
          LOAD_MORE_PAGE_SIZE,
          reduxAdvertisersRef.current,
          filterValue,
          filterType,
          agenciesExpandedRef.current,
          emailIndexLastKey,
          (newEmailIndexLastKey) => setEmailIndexLastKey(newEmailIndexLastKey),
          () => setLoadedLastEmailIndex(true)
        )
      );
    } catch (err) {
      dispatch(
        LaunchError(err, false, [], {
          functionName: "runPaginatedFetch",
          capture: "exception",
        })
      );
      console.error("Error in loadMoreClients:", err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    dispatch,
    lastKey,
    emailIndexLastKey,
    loadedLastAdvertiser,
    loadedLastEmailIndex,
    isLoadingMore,
    getEffectiveFilter,
  ]);

  // Initial load only — filter/search changes use the dedicated effect below.
  useEffect(() => {
    if (didMountInitialFetchRef.current) return;
    didMountInitialFetchRef.current = true;
    // Drop stale advertisers left in Redux from a previous dashboard visit so
    // the first page fetch does not merge on top of leftover keys.
    dispatch(getAdvertisers({}));
    const { filterValue } = getEffectiveFilter();
    const generation = ++fetchGenerationRef.current;
    fetchInitialData(filterValue, generation);
    // Mount-only: filter/search changes are handled by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchInitialData]);

  // Consolidate advertisers with full data
  const advertisers = useMemo(() => {
    const { signature } = getEffectiveFilter();

    // Do not render stale Redux from a previous query while a new fetch is in flight.
    if (signature !== loadedFilterSignature) {
      return {};
    }

    const consolidated = {};

    Object.entries(reduxAdvertisers || {}).forEach(([id, baseAdvertiser]) => {
      if (
        baseAdvertiser?.isDemo === true ||
        baseAdvertiser?.isDeleted === true ||
        baseAdvertiser?.createdFrom === "wacm"
      ) {
        return;
      }

      const fullData = advertiserFullData[id] || { isLoading: true };

      consolidated[id] = {
        // Base data from Redux
        ...baseAdvertiser,
        // Loading state
        isLoading: fullData.isLoading ?? true,
        // Full data (null until loaded)
        loginMetrics: fullData.loginMetrics || null,
        campaigns: fullData.campaigns || null,
        hasRedCampaignAlerts: fullData.hasRedCampaignAlerts || false,
        redCampaignAlerts: fullData.redCampaignAlerts || [],
        kpiSummary: getEffectiveKpiSummary({
          manual: baseAdvertiser.kpiSummary,
          derived: fullData.campaigns?.kpiSummary ?? null,
        }),
        optimizations: fullData.optimizations || null,
        metrics: fullData.metrics || null,
        npsRating: fullData.npsRating ?? null,
        activeUsers: fullData.activeUsers ?? null,
        healthScore: fullData.healthScore ?? null,
        aiCreativesLast30Days: fullData.aiCreativesLast30Days ?? null,
        // Convenience accessors
        users: fullData.loginMetrics?.users || [],
      };
    });

    return consolidated;
  }, [
    reduxAdvertisers,
    advertiserFullData,
    getEffectiveFilter,
    loadedFilterSignature,
  ]);

  // Group clients by agencyID for visual organization
  const groupClientsByAgency = useCallback((clients) => {
    if (!clients || clients.length === 0) return [];

    const agencyGroups = {};

    clients.forEach((client) => {
      const agencyID = client.advertiser?.agencyID || "unknown";

      if (!agencyGroups[agencyID]) {
        agencyGroups[agencyID] = {
          type: "agency",
          agencyID: agencyID,
          advertisers: [],
        };
      }

      agencyGroups[agencyID].advertisers.push({
        id: client.id,
        advertiser: client.advertiser,
      });
    });

    // Convert to array and maintain order (first advertiser's agency appears first)
    return Object.values(agencyGroups);
  }, []);

  // When the user is searching, AppSumo accounts and $99-plan clients stay visible.
  // With an empty search box, those brands are hidden from the table (all / active / inactive).
  const hasActiveSearchForTable = Boolean(
    searchTerm && String(searchTerm).trim().length >= MIN_SEARCH_LENGTH
  );

  // Order matches Redux merge (server / pagination); no client-side health re-sort
  const displayedClients = useMemo(() => {
    const list = Object.entries(advertisers).map(([id, advertiser]) => ({
      id,
      advertiser,
    }));
    if (hasActiveSearchForTable) {
      return list;
    }
    return list.filter((client) => {
      const advertiser = client?.advertiser;
      const agencyProfile = getAgencyProfile(advertiser?.agencyID);
      const isAppSumo = agencyProfile?.isAppSumo ?? advertiser?.isAppSumo;
      if (isAppSumo === true) return false;
      if (hasHiddenDefaultClientsListPlanPrice(advertiser)) return false;
      return true;
    });
  }, [advertisers, hasActiveSearchForTable, getAgencyProfile]);

  // Clients grouped for health summary cards: one bucket unit per agency group.
  // Multi-advertiser groups use the rounded average score only after every advertiser has loaded.
  const healthSummaryGroups = useMemo(() => {
    const groups = groupClientsByAgency(displayedClients).filter(
      (agencyGroup) => (agencyGroup?.advertisers || []).length > 0
    );

    return groups.map((group) => {
      const { healthSummaryScore, isHealthSummaryLoading } =
        getAgencyHealthFromAdvertisers(group.advertisers);
      return {
        ...group,
        healthSummaryScore,
        isHealthSummaryLoading,
      };
    });
  }, [displayedClients, groupClientsByAgency]);

  // Defensive filter to keep agency grouping consistent with status filter.
  // Agencies stay intact when any member matches; solo/unknown rows filter individually.
  // Name search must not apply plan/status filters — only the name match matters.
  const clientsForGrouping = useMemo(() => {
    if (filterStatus === null || hasActiveSearchForTable) {
      return displayedClients;
    }

    const groups = groupClientsByAgency(displayedClients);
    const keptAgencyIds = new Set();

    for (const group of groups) {
      if (!isValidAgencyIDForGrouping(group.agencyID)) {
        continue;
      }
      const hasMatchingMember = group.advertisers.some(({ advertiser }) =>
        matchesClientsListStatusFilter({ advertiser, filterStatus })
      );
      if (hasMatchingMember) {
        keptAgencyIds.add(group.agencyID);
      }
    }

    return (displayedClients || []).filter((client) => {
      const agencyID = client.advertiser?.agencyID || "unknown";
      if (isValidAgencyIDForGrouping(agencyID)) {
        return keptAgencyIds.has(agencyID);
      }
      return matchesClientsListStatusFilter({
        advertiser: client.advertiser,
        filterStatus,
      });
    });
  }, [
    displayedClients,
    filterStatus,
    hasActiveSearchForTable,
    groupClientsByAgency,
  ]);

  // Group by agency: server order between agencies; health asc within each agency
  const groupedClients = useMemo(() => {
    const groups = groupClientsByAgency(clientsForGrouping).filter(
      (agencyGroup) => (agencyGroup?.advertisers || []).length > 0
    );
    return mapAgencyGroupsWithSortedAdvertisers(groups);
  }, [clientsForGrouping, groupClientsByAgency]);

  const agencyIDsToFetch = useMemo(() => {
    if (!groupedClients || groupedClients.length === 0) return [];

    const ids = new Set();

    groupedClients.forEach((group) => {
      const agencyID = group.agencyID;
      if (!agencyID || agencyID === "unknown") return;
      if (getAgencyEntry(agencyID)?.profileLoaded) return;
      ids.add(agencyID);
    });

    return Array.from(ids);
  }, [groupedClients, getAgencyEntry]);

  const fetchAgencyProfiles = useCallback(
    async (agencyIDs, isCancelledCheck) => {
      try {
        const result = await dispatch(startGetAgenciesNames(agencyIDs));
        if (!isCancelledCheck() && result.ok && result.data?.agenciesByAgency) {
          Object.entries(result.data.agenciesByAgency).forEach(
            ([agencyID, profile]) => {
              if (!profile) return;
              mergeAgencyCache(
                agencyID,
                {
                  name: profile.name,
                  isAppSumo: profile.isAppSumo ?? false,
                  discoverySource: profile.discoverySource ?? null,
                  imageProvider: resolveAgencyImageProvider(
                    profile.imageProvider
                  ),
                  hasMinimumTermContractClause:
                    profile.hasMinimumTermContractClause === true,
                  profileLoaded: true,
                },
                { syncState: true }
              );
            }
          );
        }
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "runPaginatedFetch",
            capture: "exception",
          })
        );
        if (!isCancelledCheck()) {
          console.error("Error fetching agency profiles:", error);
        }
      }
    },
    [dispatch, mergeAgencyCache]
  );

  useEffect(() => {
    if (agencyIDsToFetch.length === 0) return;

    let isCancelled = false;

    fetchAgencyProfiles(agencyIDsToFetch, () => isCancelled);

    return () => {
      isCancelled = true;
    };
  }, [agencyIDsToFetch, fetchAgencyProfiles]);

  const enrichedGroupedClients = useMemo(() => {
    return groupedClients.map((agencyGroup) => {
      const agencyProfile = getAgencyProfile(agencyGroup.agencyID);
      return {
        ...agencyGroup,
        agencyName: agencyProfile?.name ?? null,
        agencyIsAppSumo: agencyProfile?.isAppSumo ?? false,
        agencyDiscoverySource: agencyProfile?.discoverySource ?? null,
        agencyImageProvider: resolveAgencyImageProvider(
          agencyProfile?.imageProvider
        ),
        agencyHasMinimumTermContractClause:
          agencyProfile?.hasMinimumTermContractClause === true,
      };
    });
  }, [groupedClients, getAgencyProfile]);

  const hasMoreItems = hasActiveSearchForTable
    ? !(loadedLastAdvertiser && loadedLastEmailIndex)
    : !loadedLastAdvertiser;

  return {
    displayedClients,
    groupedClients: enrichedGroupedClients,
    healthSummaryGroups,
    isLoading,
    isLoadingMore,
    hasMoreItems,
    loadMoreClients,
    totalClients: displayedClients.length,
    filterStatus,
    setFilterStatus,
    searchTerm,
    setSearchTerm,
  };
};

export default useClientsList;
